import type { PositionLeg } from '../../shared/types';
import { rollupPositions } from '../../shared/positions';

export type LegDraft = {
  key: string;
  broker: string;
  quantity: string;
  avgPrice: string;
};

let legKeySeq = 0;
export function newLegKey(): string {
  legKeySeq += 1;
  return `leg-${legKeySeq}`;
}

export function legsFromHolding(opts: {
  positions?: PositionLeg[];
  quantity?: number | null;
  avgPrice?: number | null;
}): LegDraft[] {
  if (opts.positions && opts.positions.length > 0) {
    return opts.positions.map((p) => ({
      key: newLegKey(),
      broker: p.broker ?? '',
      quantity: fmtNum(p.quantity),
      avgPrice: fmtNum(p.avg_price),
    }));
  }
  return [
    {
      key: newLegKey(),
      broker: '',
      quantity: fmtNum(opts.quantity),
      avgPrice: fmtNum(opts.avgPrice),
    },
  ];
}

export function emptyLeg(): LegDraft {
  return { key: newLegKey(), broker: '', quantity: '', avgPrice: '' };
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '';
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(6)));
}

/** Parse drafts into PositionLeg[]; returns error string on failure. */
export function parseLegDrafts(
  drafts: LegDraft[],
  opts: { cashEq: boolean }
): { legs: PositionLeg[] } | { error: string } {
  const legs: PositionLeg[] = [];
  for (const d of drafts) {
    const q = Number(d.quantity);
    if (!Number.isFinite(q) || q <= 0) {
      return { error: 'Each position quantity must be a positive number' };
    }
    const leg: PositionLeg = {
      quantity: q,
      broker: d.broker.trim() ? d.broker.trim() : null,
    };
    if (!opts.cashEq) {
      if (d.avgPrice.trim() === '') {
        leg.avg_price = null;
      } else {
        const p = Number(d.avgPrice);
        if (!Number.isFinite(p) || p <= 0) {
          return { error: 'Each position avg price must be a positive number or empty' };
        }
        leg.avg_price = p;
      }
    }
    legs.push(leg);
  }
  try {
    rollupPositions(legs, { cashEq: opts.cashEq });
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Invalid positions' };
  }
  return { legs };
}

export function draftRollup(
  drafts: LegDraft[],
  opts: { cashEq: boolean }
): { quantity: number | null; avgPrice: number | null } {
  const parsed = parseLegDrafts(drafts, opts);
  if ('error' in parsed) return { quantity: null, avgPrice: null };
  try {
    const r = rollupPositions(parsed.legs, { cashEq: opts.cashEq });
    return { quantity: r.quantity, avgPrice: r.avg_price };
  } catch {
    return { quantity: null, avgPrice: null };
  }
}

type Props = {
  legs: LegDraft[];
  cashEq: boolean;
  ccyLabel?: string;
  saving?: boolean;
  onChange: (legs: LegDraft[]) => void;
};

export function PositionLegsEditor({ legs, cashEq, ccyLabel, saving, onChange }: Props) {
  const multi = legs.length > 1;
  const summary = draftRollup(legs, { cashEq });

  function update(key: string, patch: Partial<LegDraft>) {
    onChange(legs.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function remove(key: string) {
    if (legs.length <= 1) return;
    onChange(legs.filter((l) => l.key !== key));
  }

  return (
    <div class="pos-legs">
      {legs.map((leg, i) => (
        <div class={`pos-leg${multi ? ' pos-leg-card' : ''}`} key={leg.key}>
          {multi && (
            <div class="pos-leg-head">
              <span class="edit-pop-label">Position {i + 1}</span>
              <button
                type="button"
                class="edit-pop-btn edit-pop-btn-ghost pos-leg-remove"
                onClick={() => remove(leg.key)}
                disabled={saving}
              >
                Remove
              </button>
            </div>
          )}
          {multi && (
            <div class="edit-pop-row">
              <label class="edit-pop-label">Broker</label>
              <input
                class="edit-pop-input mono"
                placeholder="ibkr, fio, …"
                value={leg.broker}
                onInput={(e) => update(leg.key, { broker: (e.target as HTMLInputElement).value })}
                disabled={saving}
                autoComplete="off"
              />
            </div>
          )}
          <div class="edit-pop-row">
            <label class="edit-pop-label">Quantity</label>
            <input
              class="edit-pop-input mono"
              type="number"
              step="any"
              min="0"
              value={leg.quantity}
              onInput={(e) => update(leg.key, { quantity: (e.target as HTMLInputElement).value })}
              disabled={saving}
            />
          </div>
          {!cashEq && (
            <div class="edit-pop-row">
              <label class="edit-pop-label">
                Avg price{ccyLabel ? ` (${ccyLabel})` : ''}
              </label>
              <input
                class="edit-pop-input mono"
                type="number"
                step="any"
                min="0"
                placeholder="—"
                value={leg.avgPrice}
                onInput={(e) => update(leg.key, { avgPrice: (e.target as HTMLInputElement).value })}
                disabled={saving}
              />
            </div>
          )}
        </div>
      ))}

      <button
        type="button"
        class="edit-pop-btn edit-pop-btn-ghost pos-leg-add"
        onClick={() => {
          const next = [...legs];
          // Promote first leg to show broker when entering multi mode.
          if (next.length === 1 && next[0] && next[0].broker === '') {
            /* broker field appears automatically when multi */
          }
          onChange([...next, emptyLeg()]);
        }}
        disabled={saving}
      >
        Another position
      </button>

      {multi && (
        <div class="pos-leg-summary mono">
          Total {summary.quantity != null ? summary.quantity : '—'}
          {!cashEq && (
            <>
              {' · '}
              avg {summary.avgPrice != null ? Number(summary.avgPrice.toFixed(6)) : '—'}
            </>
          )}
        </div>
      )}
    </div>
  );
}
